import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticle, getRelatedArticles, getArticles } from '../services/contentService';
import { Article, ArticleWithAuthor } from '../types';
import { Sparkles, Share2, Bookmark, ChevronRight, Hash, Copy, Check } from 'lucide-react';
import { generateBrief } from '../services/geminiService';
import { SEOHead } from '../components/SEOHead';
import { ArticleCard } from '../components/ArticleCard';
import { analytics } from '../services/analyticsService';
import { VideoEmbed } from '../components/VideoEmbed';
import { isYouTubeUrl, getVimeoId, getTikTokId, getInstagramId, getFacebookId, getXId } from '../utils/mediaUtils';
import { CommentsSection } from '../components/CommentsSection';

export const ArticleView: React.FC = () => {
  const { id } = useParams<{ category: string; id: string }>();
  const [article, setArticle] = useState<ArticleWithAuthor | undefined | null>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [related, setRelated] = useState<Article[]>([]);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Analytics State
  const startTime = useRef(Date.now());
  const maxScroll = useRef(0);

  useEffect(() => {
    if (id) {
      // Reset state for new article nav
      setArticle(undefined);
      setAiSummary(null);
      setCopied(false);
      window.scrollTo(0, 0);

      console.log(`[ARTICLE DEBUG] Fetching article ID: ${id}`);
      // Reset Analytics
      startTime.current = Date.now();
      maxScroll.current = 0;

      getArticle(id).then(data => {
        console.log(`[ARTICLE DEBUG] Fetch Result:`, data);
        if (!data) {
          setArticle(null);
          setError("Story not found. It may have been archived or moved.");
          return;
        }
        setArticle(data as ArticleWithAuthor);
        setError(null);
        getRelatedArticles(data.id).then(res => {
          if (res && res.length > 0) {
            setRelated(res);
          } else {
            // Fallback: Get latest news if no specific related articles
            getArticles(undefined, false, 4).then(latest => {
              setRelated(latest.filter(a => a.id !== data.id).slice(0, 4));
            });
          }
        });
      }).catch(err => {
        console.error(err);
        setArticle(null);
        setError("Unable to retrieve this story at the moment.");
      });
    }

    // Cleanup / Analytics Logger
    return () => {
      if (id) {
        const timeSpent = Math.floor((Date.now() - startTime.current) / 1000);
        analytics.logArticleEngagement(id, maxScroll.current, timeSpent);
      }
    };
  }, [id]);

  // Scroll Listener for Depth Tracking
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPosition = window.scrollY;
      const percentage = Math.min(100, Math.max(0, Math.round((scrollPosition / totalHeight) * 100)));

      if (percentage > maxScroll.current) {
        maxScroll.current = percentage;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGenerateBrief = async () => {
    if (!article) return;
    setIsGenerating(true);
    const summary = await generateBrief(article.content);
    setAiSummary(summary);
    setIsGenerating(false);
  };

  const handleShare = async () => {
    if (!article) return;
    const url = window.location.href;
    const shareData = {
      title: article.title,
      text: article.excerpt,
      url: url,
    };

    // Navigator.share requires an absolute URL with http/https scheme.
    // Sandboxed environments often use blob: or about: schemes which throw "Invalid URL".
    const isShareableUrl = url.startsWith('http');

    if (navigator.share && isShareableUrl) {
      try {
        await navigator.share(shareData);
        return; // Success
      } catch (err) {
        console.error('Share failed, falling back to clipboard:', err);
      }
    }

    // Fallback: Clipboard Copy
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (clipboardErr) {
      console.error('Clipboard copy failed:', clipboardErr);
    }
  };

  if (article === null) return (
    <div className="h-96 flex flex-col items-center justify-center gap-4 text-center px-4">
      <div className="text-brand-900 font-serif text-2xl font-black">Something went wrong.</div>
      <p className="text-gray-500 font-serif italic">{error}</p>
      <Link to="/" className="mt-4 text-xs font-bold text-brand-500 uppercase tracking-widest hover:text-brand-900">Return Home</Link>
    </div>
  );

  if (!article) return (
    <div className="h-96 flex flex-col items-center justify-center text-gray-400 gap-2">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500 mb-2"></div>
      <div className="text-xs uppercase tracking-widest font-bold">Loading Story...</div>
      <div className="text-[10px] opacity-40 font-mono">Reference: {id}</div>
    </div>
  );

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        image={article.coverImage}
        type="article"
        publishedTime={article.publishedAt}
        author={article.author?.name || 'Nida Staff'}
      />

      <article className="max-w-3xl mx-auto pb-20 animate-fade-in">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-6">
          <Link to="/" className="hover:text-brand-500">Home</Link>
          <ChevronRight size={10} />
          <Link to={`/news/${(article.category || 'news').toLowerCase()}`} className="hover:text-brand-500">{article.category || 'News'}</Link>
        </nav>

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-5xl font-serif font-black text-brand-900 leading-tight mb-6">{article.title}</h1>
          <p className="text-xl text-gray-500 font-serif leading-relaxed mb-6 italic">{article.excerpt}</p>

          <div className="flex items-center justify-center gap-4 py-6 border-y border-gray-100">
            {article.author?.avatar && (
              <img src={article.author.avatar} className="w-12 h-12 rounded-full border border-gray-100" alt="author" />
            )}
            {!article.author?.avatar && (
              <div className="w-12 h-12 rounded-full bg-brand-100 border border-brand-200 flex items-center justify-center text-brand-500 font-bold uppercase text-sm">
                {(article.author?.name || 'NS')[0]}
              </div>
            )}
            <div className="text-left">
              <div className="font-bold text-brand-900 font-sans">{article.author?.name || 'Nida Staff'}</div>
              <div className="text-xs text-gray-500 font-sans font-medium uppercase tracking-wide">
                {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'Recently Released'} • {article.readTime} min read
              </div>
            </div>
          </div>
        </div>

        <div className="relative aspect-video w-full rounded-xl overflow-hidden mb-12 shadow-lg">
          {article.coverImage ? (
            <img
              src={article.coverImage}
              className="w-full h-full object-cover"
              alt={article.title}
              loading="eager" // LCP
              decoding="async"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 font-serif">
              No Cover Image Provided
            </div>
          )}
        </div>

        {/* AI Feature Block */}
        <div className="bg-brand-100/50 border border-brand-500/20 rounded-xl p-6 mb-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-brand-500 font-bold">
              <Sparkles size={18} />
              <span>AI Smart Brief</span>
            </div>
            {!aiSummary && (
              <button
                onClick={handleGenerateBrief}
                disabled={isGenerating}
                className="text-xs bg-white border border-gray-200 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium text-brand-900 shadow-sm"
              >
                {isGenerating ? 'Analyzing...' : 'Generate Summary'}
              </button>
            )}
          </div>

          {aiSummary ? (
            <div className="prose prose-sm prose-ul:list-disc prose-li:mb-2 text-brand-900 font-serif" dangerouslySetInnerHTML={{ __html: aiSummary }} />
          ) : (
            <p className="text-sm text-gray-500 italic font-serif">Get a quick 3-point summary of this article powered by Gemini.</p>
          )}
        </div>

        {article.videoUrl && (
          <VideoEmbed url={article.videoUrl} caption={article.videoCaption} />
        )}

        {/* Content Image Support */}
        {article.contentImageUrl && (
          <div className="mb-12">
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
              <img
                src={article.contentImageUrl}
                className="w-full h-auto object-cover"
                alt={article.contentImageCaption || article.title}
                loading="lazy"
              />
            </div>
            {article.contentImageCaption && (
              <p className="mt-3 text-sm text-gray-500 italic text-center font-serif">
                {article.contentImageCaption}
              </p>
            )}
          </div>
        )}

        {/* Multiple Content Images Support */}
        {article.contentImages && article.contentImages.length > 0 && (
          <div className="mb-12 space-y-8">
            {article.contentImages.map((img, idx) => (
              <div key={idx}>
                <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
                  <img
                    src={img.url}
                    className="w-full h-auto object-cover"
                    alt={img.caption || `Image ${idx + 1}`}
                    loading="lazy"
                  />
                </div>
                {img.caption && (
                  <p className="mt-3 text-sm text-gray-500 italic text-center font-serif">
                    {img.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Article Content */}
        <div className="prose prose-lg prose-serif max-w-none text-gray-800 leading-8 selection:bg-brand-100 selection:text-brand-900 whitespace-pre-wrap">
          {(() => {
            const content = article.content || '<p>No content available.</p>';

            // Logic to split by 16:9 or 9:16 friendly video URLs on their own lines
            // We look for URLs inside <p> tags or just raw URLs
            const videoPattern = /(?:<p>)?\s*(https?:\/\/(?:www\.|web\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/|vimeo\.com\/|tiktok\.com\/@[\w.-]+\/video\/|instagram\.com\/(?:p|reels|tv)\/|facebook\.com\/(?:watch\?v=|videos\/|share\/r\/|reels\/|[\w.]+\/videos\/)|(?:twitter\.com|x\.com)\/\w+\/status\/)\S+)\s*(?:<\/p>)?/g;

            const parts = content.split(videoPattern);
            return parts.map((part, index) => {
              const trimmed = part.trim();
              if (isYouTubeUrl(trimmed) || getVimeoId(trimmed) || getTikTokId(trimmed) || getInstagramId(trimmed) || getFacebookId(trimmed) || getXId(trimmed)) {
                return <VideoEmbed key={index} url={trimmed} />;
              }
              return (
                <div
                  key={index}
                  className={index === 0 ? "first-letter:text-5xl first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:mt-[-4px] first-letter:text-brand-900" : ""}
                  dangerouslySetInnerHTML={{ __html: part }}
                />
              );
            });
          })()}
        </div>

        {article.tags && Array.isArray(article.tags) && article.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2">
            {article.tags.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-wide rounded-full hover:bg-brand-100 hover:text-brand-900 cursor-pointer transition-colors">
                <Hash size={10} /> {tag}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex justify-between items-center">
          <div className="flex gap-6">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-gray-500 hover:text-brand-500 transition-colors font-medium text-sm"
            >
              {copied ? <Check size={18} className="text-green-500" /> : <Share2 size={18} />}
              {copied ? 'Copied Link' : 'Share Article'}
            </button>
            <button className="flex items-center gap-2 text-gray-500 hover:text-brand-500 transition-colors font-medium text-sm">
              <Bookmark size={18} /> Save for Later
            </button>
          </div>
        </div>

        {/* Comments Section */}
        <CommentsSection articleId={article.id} />
      </article>

      {/* Related Content */}
      {related.length > 0 && (
        <section className="bg-gray-50 py-16 border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4">
            <h3 className="text-xl font-bold uppercase tracking-widest text-brand-900 mb-8">Related Stories</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {related.map(item => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};
